import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteTag, listTags } from '../../api/business/endpoints';
import type { Tag } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { TagDialog } from './TagDialog';

type TagSortKey = 'name' | 'products';

const VALUE_OF: Record<TagSortKey, (tag: Tag) => string | number | null> = {
  name: (tag) => tag.name,
  products: (tag) => tag.productCount,
};

type Dialog = { kind: 'tag'; tag: Tag | null } | { kind: 'delete'; tag: Tag };

/**
 * The company's tags (R0030 WP-06) — on sale, new, organic — with how many products carry each. The whole list
 * is held, so it sorts in the browser (Conventions/08 §3).
 */
export const TagsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const [term, setTerm] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const sort = useSort<TagSortKey>('name');
  const { languages } = useContentLanguages();
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: ['tags', company?.id],
    enabled: company !== null,
    queryFn: () => listTags(company?.id ?? ''),
  });
  const remove = useAppMutation(
    (tagId: string) => deleteTag(company?.id ?? '', tagId),
    ['tags', 'product', 'products'],
    () => setDialog(null),
  );

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('tags.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (tag) =>
        needle === '' ||
        Object.values(tag.translations).some((texts) =>
          (texts.name ?? '').toLocaleLowerCase('sk').includes(needle),
        ),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <>
      <PageHeader
        title={t('tags.title')}
        subtitle={t('tags.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialog({ kind: 'tag', tag: null })}
              >
                {t('tags.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={query.error ?? selection.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('tags.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  {t('tags.name')}
                </SortableCell>
                <SortableCell sort={sort} column="products" align="right">
                  {t('tags.products')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={3} />
              ) : (
                rows.map((tag) => (
                  <TableRow key={tag.id} hover>
                    <TableCell sx={{ fontWeight: 560 }}>
                      {tag.name}
                      <MissingLanguagesChip
                        missing={missingLanguages(textsOf(tag.translations, 'name', languages), languages)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={tag.productCount === 0}
                        onClick={() => navigate(`/products?tagId=${tag.id}`)}
                      >
                        {tag.productCount}
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      {canManage ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={() => setDialog({ kind: 'tag', tag })}>
                            {t('tags.edit')}
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            onClick={() => setDialog({ kind: 'delete', tag })}
                          >
                            {t('common.delete')}
                          </Button>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState
            title={t('tags.empty')}
            detail={canManage ? t('tags.emptyManage') : t('tags.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'tag', tag: null })}>
                  {t('tags.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>

      {dialog?.kind === 'tag' && company ? (
        <TagDialog companyId={company.id} tag={dialog.tag} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          title={t('tags.deleteTitle', { name: dialog.tag.name })}
          message={t('tags.deleteMessage', { count: dialog.tag.productCount })}
          confirmLabel={t('common.delete')}
          onConfirm={() => remove.mutate(dialog.tag.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </>
  );
};
